/**
 * Local persistence for an in-flight `chunkedUpload` session, so a
 * page reload (or the user closing the modal) doesn't force them to
 * re-upload a 1.7 GB practice from scratch.
 *
 * Single active slot per user. Picking a new file before completing
 * the previous one implicitly abandons the previous — the common case
 * is one upload at a time, and supporting multi-concurrent uploads
 * would conflict with the worker pool's bandwidth assumption.
 *
 * Storage backing matches the rest of the app: localStorage on web,
 * AsyncStorage on native. The doc mentions IndexedDB but the payload
 * is ~120 bytes JSON — localStorage is fine and keeps the dependency
 * surface flat (see `src/utils/storage.ts` for the same pattern).
 *
 * BE-side `sessionId` TTL is 24h (`ChunkedUploadService.cs:SessionExpiry`).
 * We mirror that here so a stale slot from yesterday doesn't trigger a
 * misleading "Continue X?" prompt.
 */
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const TTL_MS = 24 * 60 * 60 * 1000;

const keyForUser = (userId: string) => `agape_chunked_upload_resume_${userId}`;

export interface UploadResumeEntry {
  sessionId: string;
  fileName: string;
  fileSize: number;
  /** From `POST /init` — needed to slice the local File against the BE's grid. */
  totalChunks: number;
  chunkSize: number;
  /** ms since epoch. Compared against TTL_MS on read. */
  createdAt: number;
}

async function readRaw(key: string): Promise<string | null> {
  if (Platform.OS === 'web') {
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
    catch { return null; }
  }
  try { return await AsyncStorage.getItem(key); }
  catch { return null; }
}

async function writeRaw(key: string, value: string): Promise<void> {
  if (Platform.OS === 'web') {
    try { if (typeof localStorage !== 'undefined') localStorage.setItem(key, value); }
    catch { /* quota / SSR — non-fatal */ }
    return;
  }
  try { await AsyncStorage.setItem(key, value); }
  catch { /* non-fatal */ }
}

async function removeRaw(key: string): Promise<void> {
  if (Platform.OS === 'web') {
    try { if (typeof localStorage !== 'undefined') localStorage.removeItem(key); }
    catch { /* non-fatal */ }
    return;
  }
  try { await AsyncStorage.removeItem(key); }
  catch { /* non-fatal */ }
}

export const uploadResume = {
  /**
   * Returns the stored resume entry for the given user, or `null` when
   * none exists / it's expired beyond the 24h TTL. Expired entries are
   * deleted as a side effect of the read so they don't keep triggering
   * stale "Continue X?" prompts.
   */
  async get(userId: string): Promise<UploadResumeEntry | null> {
    const raw = await readRaw(keyForUser(userId));
    if (!raw) return null;
    let parsed: UploadResumeEntry;
    try { parsed = JSON.parse(raw) as UploadResumeEntry; }
    catch { await removeRaw(keyForUser(userId)); return null; }
    if (!parsed.sessionId || typeof parsed.createdAt !== 'number') {
      await removeRaw(keyForUser(userId));
      return null;
    }
    if (Date.now() - parsed.createdAt > TTL_MS) {
      await removeRaw(keyForUser(userId));
      return null;
    }
    return parsed;
  },

  /**
   * Persist a new resume entry, overwriting any prior slot. Call right
   * after `POST /init` succeeds so the sessionId is recoverable from
   * the next reload.
   */
  async set(userId: string, entry: Omit<UploadResumeEntry, 'createdAt'>): Promise<void> {
    const payload: UploadResumeEntry = { ...entry, createdAt: Date.now() };
    await writeRaw(keyForUser(userId), JSON.stringify(payload));
  },

  /**
   * Drop the resume entry — call after a successful `/complete`, after
   * the user explicitly dismisses the "Continue?" prompt, or after an
   * unrecoverable error that the user acknowledges.
   */
  async clear(userId: string): Promise<void> {
    await removeRaw(keyForUser(userId));
  },
};
